import { useEffect, useRef, useState } from "react";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { format } from "helpers/time/format";
import RegisterToEvent from "modules/eventLanding/eventRegistration/RegisterToEvent";
import classNames from "classnames";
import { useI18n } from "i18n";
import { IEvent } from "modules/event/types";
import { selectIsMobile } from "modules/deviceInfo";
import { useSelector } from "react-redux";
import { CustomQuestionsForm } from "modules/registration/components/CustomQuestionsForm";
import { selectShowRegistrationForm } from "modules/registration/redux/selectors";
import { PlaceTicketOrderDialog } from "modules/ticketing/components/PlaceTicketOrderDialog";
import { EventDetailsButtons } from "./EventDetailsButtons";
import EventLabel from "./EventLabel";
import EventTime from "./EventTime";
import { LandingPageCard as Card } from "../components";
import { useStyles } from "./styles";

interface Props {
  eventData: IEvent;
  currentTime: number;
  isEventFull?: boolean;
  timezone: string;
  isEnded: boolean;
  amIRegistered: boolean;
  showJoinEventButton: boolean;
  isManager: boolean;
  isSpeaker: boolean;
  isLobbyEnabled: boolean;
  isEventOpen: boolean;
  showRegisterDialog: boolean;
  handleJoinAsSpeaker: () => void;
  handleJoinEvent: () => void;
  handleJoinLobby: () => void;
  onAddToCalendarClick: () => void;
  className?: string;
}

const EventDetails = ({
  eventData,
  currentTime,
  isEventFull = false,
  timezone,
  amIRegistered,
  isEnded,
  isManager,
  isSpeaker,
  isLobbyEnabled,
  isEventOpen,
  showJoinEventButton,
  showRegisterDialog,
  handleJoinAsSpeaker,
  handleJoinEvent,
  handleJoinLobby,
  onAddToCalendarClick,
  className,
}: Props): JSX.Element => {
  const showCustomRegistrationForm = useSelector(selectShowRegistrationForm);
  const {
    startTime,
    endTime,
    isPrivate = false,
    name = "",
    id,
    isUnlimitedEvent = false,
  } = eventData;
  const [countdownFinished, setCountdownFinished] = useState(false);
  const { t } = useI18n(["eventLanding", "event"]);
  const cardRef = useRef<HTMLDivElement>(null);
  const classes = useStyles();

  const isMobileView = useSelector(selectIsMobile);

  useEffect(() => {
    if (countdownFinished) {
      handleJoinEvent();
    }
  }, [countdownFinished, handleJoinEvent]);

  useEffect(() => {
    cardRef?.current?.focus();
  }, []);

  if (isMobileView && showCustomRegistrationForm) {
    return <CustomQuestionsForm eventId={id} eventData={eventData} />;
  }

  if (isMobileView && showRegisterDialog) {
    return (
      <Container top={20} data-testid="ev-register-cnt">
        <RegisterToEvent
          onAddToCalendar={onAddToCalendarClick}
          event={eventData}
        />
      </Container>
    );
  }

  return (
    <Card
      ref={cardRef}
      elevation={0}
      title={
        <Typography
          className={classes.eventTitle}
          variant="h1"
          aria-label={t("event.accessibility.details", {
            eventName: name,
          })}
        >
          {eventData.name}
        </Typography>
      }
      justifyContent="space-between"
      alignItems="stretch"
      column
      maxWidth="sm"
      data-testid="ev-details-card"
      className={classNames(classes.root, className)}
    >
      {isUnlimitedEvent ? (
        <Typography
          variant="h5"
          aria-label={t("event.accessibility.startTime")}
        >
          {t("event:24.7.space")}
        </Typography>
      ) : (
        <Container className={classes.timeContainer}>
          <Typography
            variant="h5"
            aria-label={t("event.accessibility.startTime")}
          >
            {t("event.startTime.label")}
          </Typography>
          <Typography variant="h5">
            {`${format(startTime, "MMMM Do, hh:mma")} - ${timezone}`}
          </Typography>
          <Typography
            variant="h5"
            aria-label={t("event.accessibility.endTime")}
          >
            {t("event.endTime.label")}
          </Typography>
          <Typography variant="h5">
            {`${format(endTime, "MMMM Do, hh:mma")} - ${timezone}`}
          </Typography>
        </Container>
      )}
      <Container top="lg" bottom="lg">
        <EventLabel isPrivate={isPrivate} />
      </Container>
      <Container
        className={classes.countdown}
        id="register-countdown-container"
      >
        <EventTime
          startTime={startTime}
          endTime={endTime}
          currentTime={currentTime}
          className={classes.time}
          onEnd={() => {
            setCountdownFinished(true);
          }}
        />
        {!(isEnded && !isManager) && (
          <Container top={20} data-testid="event-details-buttons">
            <EventDetailsButtons
              eventId={id}
              amIRegistered={amIRegistered}
              showJoinEventButton={showJoinEventButton || countdownFinished}
              onJoinAsSpeakerClick={handleJoinAsSpeaker}
              onJoinEventClick={handleJoinEvent}
              onJoinLobbyClick={handleJoinLobby}
              isManager={isManager}
              isSpeaker={isSpeaker}
              isEventFull={isEventFull}
              isLobbyEnabled={isLobbyEnabled}
              isEventOpen={isEventOpen}
              isPrivate={isPrivate}
            />
          </Container>
        )}
        {showRegisterDialog && (
          <Container top={20} data-testid="ev-register-cnt">
            <RegisterToEvent
              onAddToCalendar={onAddToCalendarClick}
              event={eventData}
            />
          </Container>
        )}
        {showCustomRegistrationForm && (
          <CustomQuestionsForm eventId={id} eventData={eventData} />
        )}
        <PlaceTicketOrderDialog />
      </Container>
    </Card>
  );
};

export default EventDetails;
